import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { C, DEFAULT_STAMPS, scrollToId, useContent } from "./theme";
import { playThud } from "./sound";

const INKS = [C.rose, C.burgundy, "#9C7A3C", C.rich];
const SPOTS = [
  { x: 0.02, y: 0.1, rot: -12 },
  { x: 0.36, y: 0.75, rot: 8 },
  { x: 0.66, y: 0.0, rot: -4 },
  { x: 0.98, y: 0.85, rot: 14 },
];
const SPOTS_M = [
  { x: 0.02, y: 0.0, rot: -10 },
  { x: 0.9, y: 0.06, rot: 7 },
  { x: 0.1, y: 1, rot: -4 },
  { x: 0.86, y: 0.94, rot: 12 },
];

function Stamp({ s, i }) {
  const compact = typeof window !== "undefined" && window.innerWidth < 640;
  const spot = (compact ? SPOTS_M : SPOTS)[i];
  const k = compact ? 0.82 : 1;
  const ink = INKS[i % INKS.length];
  const shape = ["circle", "rect", "oval", "rect"][i % 4];
  const radius = shape === "circle" ? "9999px" : shape === "oval" ? "50%" : "6px";
  const base = shape === "circle" ? [118, 118] : shape === "oval" ? [150, 100] : [150, 92];
  const size = { width: Math.round(base[0] * k), height: Math.round(base[1] * k) };
  return (
    <motion.div
      data-testid={`passport-stamp-${i}`}
      initial={{ scale: 2.2, opacity: 0, rotate: spot.rot - 20 }}
      animate={{ scale: 1, opacity: 0.88, rotate: spot.rot }}
      exit={{ opacity: 0 }}
      transition={{ type: "spring", stiffness: 280, damping: 17, delay: i * 0.28 }}
      className="absolute flex flex-col items-center justify-center text-center"
      style={{ left: `calc((100% - ${size.width}px) * ${spot.x})`, top: `calc((100% - ${size.height}px) * ${spot.y})`, ...size, borderRadius: radius, color: ink, border: `2px solid ${ink}`, boxShadow: `inset 0 0 0 4px ${C.warm}, inset 0 0 0 5px ${ink}`, mixBlendMode: "multiply" }}
    >
      <span className="ed-mono text-[7px] tracking-[0.25em] uppercase">{s.top}</span>
      <span className="ed-serif italic text-lg leading-none my-1 px-2">{s.city}</span>
      <span className="ed-mono text-[7px] tracking-[0.25em] uppercase">{s.bottom}</span>
    </motion.div>
  );
}

export function Itinerary({ stamped, sections }) {
  const st = useContent("stamps");
  const stamps = st.items?.length ? st.items : DEFAULT_STAMPS;
  const [hover, setHover] = useState(null);
  useEffect(() => { if (stamped) stamps.slice(0, 4).forEach((_, i) => playThud(0.12 + i * 0.28)); }, [stamped]); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <div data-testid="contents-page" className="absolute inset-0 px-6 md:px-12 xl:px-16 py-8 xl:py-12 flex flex-col">
      <div className="flex items-baseline justify-between">
        <p className="ed-mono text-[9px] md:text-[10px] uppercase tracking-[0.35em]" style={{ color: C.gold }}>p.03 · Visas & itinerary</p>
        <p className="ed-mono text-[9px] uppercase tracking-[0.3em]" style={{ color: C.muted }}>No. 7J26</p>
      </div>
      <div className="relative h-[220px] sm:h-[170px] xl:h-[180px] mt-4" data-testid="passport-stamps">
        <AnimatePresence>{stamped && stamps.slice(0, 4).map((s, i) => <Stamp key={i} s={s} i={i} />)}</AnimatePresence>
      </div>
      <div className="mt-auto">
        <h2 className="ed-serif font-light leading-none mb-4" style={{ color: C.wine, fontSize: "clamp(1.8rem, 2.6vw, 2.6rem)" }}>
          Con<em style={{ color: C.rose }}>tents</em>
        </h2>
        <ol data-testid="vinyl-tracklist">
          {sections.map((s, i) => (
            <li key={s.id}>
              <button
                data-testid={`track-${s.id}`}
                onClick={() => scrollToId(s.id)}
                onMouseEnter={() => setHover(i)}
                onMouseLeave={() => setHover(null)}
                className="w-full grid grid-cols-[2.4rem_1fr_auto] items-baseline gap-2 py-2 xl:py-2 text-left group"
                style={{ borderTop: `1px solid ${C.burgundy}1a` }}
              >
                <span className="ed-mono text-[10px]" style={{ color: hover === i ? C.rose : C.gold }}>{String(i + 1).padStart(2, "0")}</span>
                <span className="ed-serif leading-tight transition-transform duration-500 group-hover:translate-x-1.5" style={{ color: C.wine, fontSize: "clamp(1.15rem, 1.6vw, 1.7rem)" }}>{s.label}</span>
                <span className="ed-mono text-[10px] tabular-nums" style={{ color: C.muted }}>{hover === i ? "→ go" : `p.${s.page}`}</span>
              </button>
            </li>
          ))}
        </ol>
      </div>
    </div>
  );
}
